/**
 * Editor Page
 *
 * Web-based documentation editor for LUKHAS
 * Supports markdown editing, version control, and GitHub publishing
 */

import React, { useState, useEffect } from 'react'
import { FileText, Plus, FolderOpen, AlertCircle } from 'lucide-react'
import { MonacoEditor } from '../components/editor/MonacoEditor'
import { EditorAPI, Draft } from '../lib/api-client'

export const EditorPage: React.FC = () => {
  const [drafts, setDrafts] = useState<Draft[]>([])
  const [currentDraft, setCurrentDraft] = useState<Draft | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showDraftList, setShowDraftList] = useState(true)

  // Load drafts on mount
  useEffect(() => {
    loadDrafts()
  }, [])

  const loadDrafts = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const { drafts: loadedDrafts } = await EditorAPI.listDrafts()
      setDrafts(loadedDrafts)
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load drafts'
      setError(errorMsg)
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreateNew = () => {
    setCurrentDraft(null)
    setShowDraftList(false)
  }

  const handleOpenDraft = async (draftId: string) => {
    try {
      const draft = await EditorAPI.getDraft(draftId)
      setCurrentDraft(draft)
      setShowDraftList(false)
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to load draft'
      setError(errorMsg)
    }
  }

  const handleSave = (draft: Draft) => {
    setCurrentDraft(draft)
    // Refresh draft list
    loadDrafts()
  }

  const handleError = (errorMsg: string) => {
    setError(errorMsg)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-gray-900 flex">
      {/* Sidebar */}
      <div
        className={`${
          showDraftList ? 'w-80' : 'w-16'
        } bg-gray-900/50 backdrop-blur-sm border-r border-gray-800 transition-all duration-300 flex flex-col`}
      >
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-800">
          <button
            onClick={() => setShowDraftList(!showDraftList)}
            className="w-full flex items-center justify-between hover:bg-gray-800/50 rounded-lg p-2 transition-colors"
          >
            <div className="flex items-center space-x-3">
              <FolderOpen className="w-5 h-5 text-blue-400" />
              {showDraftList && <span className="text-white font-medium">Drafts</span>}
            </div>
          </button>
        </div>

        {/* Sidebar Content */}
        {showDraftList && (
          <>
            {/* New Draft Button */}
            <div className="p-4 border-b border-gray-800">
              <button
                onClick={handleCreateNew}
                className="w-full flex items-center justify-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>New Draft</span>
              </button>
            </div>

            {/* Draft List */}
            <div className="flex-1 overflow-auto p-4">
              {isLoading ? (
                <div className="text-center text-gray-400 py-8">
                  <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto" />
                  <p className="mt-2 text-sm">Loading drafts...</p>
                </div>
              ) : drafts.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No drafts yet</p>
                  <p className="text-xs mt-1">Create your first draft</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {drafts.map((draft) => (
                    <button
                      key={draft.id}
                      onClick={() => handleOpenDraft(draft.id)}
                      className={`w-full text-left p-3 rounded-lg transition-all ${
                        currentDraft?.id === draft.id
                          ? 'bg-blue-500/20 border border-blue-500/50'
                          : 'bg-gray-800/50 border border-gray-700 hover:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-start space-x-2">
                        <FileText className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white truncate">
                            {draft.title}
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            {new Date(draft.updated_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-lg">
                  <FileText className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-white">
                    LUKHAS Editor
                  </h1>
                  <p className="text-gray-400 text-sm mt-0.5">
                    Λ Documentation & Knowledge Management
                  </p>
                </div>
              </div>

              {currentDraft && (
                <div className="flex items-center space-x-3 text-sm text-gray-400">
                  <span>Draft ID:</span>
                  <span className="font-mono text-gray-300">{currentDraft.id.slice(0, 8)}</span>
                </div>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-red-400 text-sm font-medium">Error</p>
                  <p className="text-red-300 text-sm mt-1">{error}</p>
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-red-400 hover:text-red-300"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Editor Area */}
        <div className="flex-1 overflow-hidden">
          {currentDraft || !showDraftList ? (
            <MonacoEditor
              draftId={currentDraft?.id}
              initialContent={currentDraft?.markdown || '# New Document\n\nStart writing...'}
              title={currentDraft?.title || 'Untitled'}
              onSave={handleSave}
              onError={handleError}
              mode="markdown"
              language="markdown"
              theme="vs-dark"
            />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No draft selected</p>
                <p className="text-sm mt-2">Select a draft from the sidebar or create a new one</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-800 bg-gray-900/50 backdrop-blur-sm">
          <div className="px-6 py-3 flex items-center justify-between text-xs text-gray-400">
            <div className="flex items-center space-x-4">
              <span>Markdown Mode</span>
              <span>•</span>
              <span>UTF-8</span>
              <span>•</span>
              <span>Line {currentDraft ? currentDraft.markdown.split('\n').length : 0}</span>
            </div>

            <div className="flex items-center space-x-4">
              <span>Version Control Enabled</span>
              <span>•</span>
              <span>GitHub Integration Ready</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EditorPage
